// eslint-disable-next-line max-classes-per-file
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { SiteService } from '../config/site.service';
import { isNullOrUndefined } from '../helper/sla-util.helper';
import { MessageService } from '../message/message.service';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { ISscCallContext, ISscInterceptorChain, SSC_INTERCEPTORS, SscInterceptor } from './ssc-interceptor';
import { SscRawRequest } from './ssc-raw-request.model';
import { SscRequest } from './ssc-request.model';
import { SscResponse } from './ssc-response.model';

@Injectable({
    providedIn: 'root',
})
export class SscClientService {

    private interceptors: SscInterceptor[];

    private chained: boolean = true;

    constructor(private myHttpClient: HttpClient,
                private mySiteService: SiteService,
                private myMessageService: MessageService,
                private myInjector: Injector,
    ) {
    }

    /**
     * ohne weitere Anpassungen absenden.
     * @param sscRequest
     * @param options
     * @param skipDefaultErrorHandling
     * @param sscCallContext
     */
    public call(sscRequest: SscRequest,
                options?: HttpRequestOptions,
                skipDefaultErrorHandling?: boolean,
                sscCallContext?: ISscCallContext): Observable<SscResponse> {

        if (this.interceptors == null) {
            this.interceptors = this.myInjector.get(SSC_INTERCEPTORS, []);
        }
        try {

            // chained variant
            if (this.chained) {
                const chain: ISscInterceptorChain = new class implements ISscInterceptorChain {

                    public position: number = 0;

                    constructor(private mySscClient: SscClientService) {
                    }

                    public doIntercept(innerSscRequest: SscRequest, innerSscCallContext: ISscCallContext): Observable<SscResponse> {
                        let response: Observable<SscResponse>;
                        if (this.position < this.mySscClient.interceptors.length) {
                            const p: number = this.position++;
                            const nextInterceptor: SscInterceptor = this.mySscClient.interceptors[ p ];
                            response = nextInterceptor.intercept(innerSscRequest, this, innerSscCallContext);
                        } else {
                            response = this.mySscClient.callBackend(innerSscRequest, options, skipDefaultErrorHandling, innerSscCallContext);
                        }
                        return response;
                    }
                }(this);
                return chain.doIntercept(sscRequest, sscCallContext);
            } else {
                // plain
                for (const i of this.interceptors) {
                    sscRequest = i.configure(sscRequest, sscCallContext);
                }
                return this.callBackend(sscRequest, options, skipDefaultErrorHandling, sscCallContext);
            }
        } catch (e) {
            console.error('SscClientService', e);
            return null;
        }
    }

    protected checkSscResponse(response: SscResponse): Observable<SscResponse> {
        // Erzeuge ein neues SscResponse Objekt, um darauf instanceof SscResponse ausführen zu können
        const sscResponse: SscResponse = new SscResponse();
        sscResponse.sscresponse = response.sscresponse;

        if (response.sscresponse.errorcode !== 0) {
            return throwError(sscResponse);
        } else {
            return of(sscResponse);
        }
    }

    protected handleError(error: HttpErrorResponse | SscResponse, skipDefaultErrorHandling: boolean): Observable<never> {
        if (!skipDefaultErrorHandling) {
            if (error instanceof SscResponse) {
                console.error(`SSC error with code ${error.sscresponse.errorcode} and message: ${error.sscresponse.errordesc}`);
                this.myMessageService.error('SSC Error', `Error code ${error.sscresponse.errorcode}: ${error.sscresponse.errordesc}`);
            } else if (error.error.sscresponse != null) {
                console.error(error);
                this.myMessageService.error('SSC Error', error.error.sscresponse.errordesc);
            } else {
                console.error(error);
                this.myMessageService.displayError(error, 'sla-core.commons.error.unknown.text');
            }
        }

        return throwError(error);
    }

    private callBackend(sscRequest: SscRequest,
                        options: HttpRequestOptions,
                        skipDefaultErrorHandling: boolean,
                        sscCallContext: ISscCallContext): Observable<SscResponse> {
        const url: string = this.buildURL(sscRequest, sscCallContext);
        const args: HttpRequestOptions = this.createOptions(options);

        const sscRawRequest: SscRawRequest = new SscRawRequest();
        sscRawRequest.module = sscRequest.getConcatModule();
        sscRawRequest.id = sscRequest.getConcatId();
        sscRawRequest.function = sscRequest.getConcatFunction();

        if (!isNullOrUndefined(sscRequest.aJwt)) {
            sscRawRequest.jwt = sscRequest.aJwt;
        }

        sscRawRequest.data = sscRequest.aData;
        if (isNullOrUndefined(sscRawRequest.data)) {
            sscRawRequest.data = {};
        }
        sscRawRequest.data[ '_table_format' ] = 'DELIM';

        return this.myHttpClient.post<SscResponse>(url, { sscrequest: sscRawRequest }, args).pipe(
            switchMap((response: SscResponse) => this.checkSscResponse(response)),
            catchError((error: HttpErrorResponse) => this.handleError(error, skipDefaultErrorHandling)),
        );
    }

    /**
     * @return complete URL like https://...../sscrest-api/module[._module]/id[._id]/function[._function]
     */
    private buildURL(sscRequest: SscRequest, sscCallContext: ISscCallContext): string {
        let url: string;
        if (!isNullOrUndefined(sscCallContext) && !isNullOrUndefined(sscCallContext[ 'ssc-rest-url' ])) {
            // explicit set for this call
            url = sscCallContext[ 'ssc-rest-url' ];
        } else if (!isNullOrUndefined(sscCallContext) && !isNullOrUndefined(sscCallContext[ 'ssc-rest-url-key' ])) {
            // indirectly set
            const key: string = sscCallContext[ 'ssc-rest-url-key' ];
            url = this.mySiteService.getConfigKey(key);
        } else {
            // from config
            url = this.mySiteService.getConfigKey('ssc-rest-url');
        }
        if (!url.endsWith('/')) { // might have been lost.
            url += '/';
        }
        url += `${sscRequest.getConcatModule()}/${sscRequest.getConcatId()}/${sscRequest.getConcatFunction()}`;
        return url;
    }

    /*
     * obsolete function, as far as I can see.
     */
    private createOptions(options: HttpRequestOptions): HttpRequestOptions {
        if (options instanceof HttpRequestOptions) {
            return options;
        } else {
            const args: HttpRequestOptions = {
                params: this.createParamsArgs(options),
                headers: this.createHeader(options),
            };
            return args;
        }
    }

    private createHeader(options: HttpRequestOptions): HttpHeaders {
        let headers: HttpHeaders = new HttpHeaders();

        if (options?.headers) {
            (options.headers as HttpHeaders).keys().forEach((key: string) => {
                const value: string = (options.headers as HttpHeaders).get(key);
                headers = headers.append(key, value);
            });
        }

        return headers;
    }

    private createParamsArgs(options?: HttpRequestOptions): HttpParams {
        let params: HttpParams = new HttpParams();

        if (options?.params) {
            const paramKeyArray: string[] = Object.keys(options.params);

            paramKeyArray.forEach((key: any) => {
                const paramValue: string = options.params[ key ];
                params = params.append(key, paramValue);
            });
        }

        return params;
    }

}
